/**
 * English copy for the six "best X" listicles.
 *
 * ── Where the named entries come from ────────────────────────────────────────
 *
 * Every product named here already has a comparison page in `lib/compare`, and
 * everything said about it is either the same thing that page says or a
 * statement about its CATEGORY. Nothing here asserts a price, a feature or a
 * version. That limit is the reason these pages can be left alone for a year
 * without going quietly wrong, which is the failure mode of every other
 * listicle in this niche.
 *
 * Where an honest list needs an option that is not an app — a hairdresser, an
 * orthodontist, a camera and a protractor — it is listed as one. Three of these
 * six lists recommend exactly that, because on those three questions no app is
 * the right answer and a list that pretends otherwise is the reason people
 * stopped trusting this format.
 */

import type { BestCopy, BestSlug } from "./types";

const DISCLOSURE =
  "Fazly wrote this page and Fazly is on this list. Everything here about another product is either a fact about what kind of product it is or the same thing our comparison page says — no prices, no feature lists, nothing that goes stale without us noticing. Where the honest answer is not us, the recommendation says so.";

const SELF = {
  name: "Fazly",
  bestFor:
    "You want the measurement and the arithmetic behind it, free, without an account, and without the photo leaving your device.",
  limitation:
    "It measures; it does not judge. Every target it scores against is a stated convention rather than a finding about attractiveness, and Fazly says so on every page — which is honest and is also less satisfying than a verdict.",
} as const;

export const en: Record<BestSlug, BestCopy> = {
  "looksmaxxing-apps": {
    title: "Best Looksmaxxing Apps in 2026 — An Honest List | Fazly",
    description:
      "Six looksmaxxing options compared honestly, including the ones that are not apps. What each is genuinely best for, what each cannot do, and which one to pick.",
    h1: "The best looksmaxxing apps, honestly",
    standfirst:
      "Six options, each with the thing it is actually best at and the thing it cannot do.",
    answer:
      "The best looksmaxxing app depends on which question you are asking. If you want a measurement you can check, Fazly runs eight facial measurements in the browser and shows the target and the weight behind every one. If you want the format the category is built around, UMAX defined it and has the largest community. If you want a written interpretation rather than a number, Qoves does that and an app cannot. If you want one clear score with nothing underneath it, LooksMax AI and Mirror are built for exactly that. And if you trust none of them, a front camera and a protractor measure the same angles, slower. No app in this category can validate its scores against attractiveness, because no ground truth for that exists — so pick on what a tool will show you, not on whose number sounds most confident.",
    disclosure: DISCLOSURE,
    entries: [
      { ...SELF, tool: "looksmax-test" },
      {
        name: "UMAX",
        bestFor:
          "You want the app the category is named after, the result format most people already recognise, and the largest community around it.",
        limitation:
          "The model that produces the score cannot be inspected, so a result you disagree with is a result you cannot investigate.",
        compare: "umax",
      },
      {
        name: "Qoves",
        bestFor:
          "You want a considered written interpretation of your face from people who do facial aesthetics professionally.",
        limitation:
          "It is somebody's time rather than arithmetic, so it is neither instant nor free — and for a question a ratio answers, that is the wrong instrument.",
        compare: "qoves",
      },
      {
        name: "LooksMax AI",
        bestFor:
          "You want one number and one screen, from an app that targets this search and does nothing else.",
        limitation:
          "The same opacity as the rest of the category: a single score with no ratios underneath it cannot be argued with.",
        compare: "looksmax-ai",
      },
      {
        name: "Mirror",
        bestFor:
          "You want a polished single-score experience and are genuinely not interested in the working behind it.",
        limitation:
          "Simplicity is the product, so there is nothing to check when the number surprises you.",
        compare: "mirror",
      },
      {
        name: "A front camera and a protractor",
        bestFor:
          "You trust none of the above and want to measure a canthal tilt or a gonial angle yourself, from a photograph you took.",
        limitation:
          "Slow, and worse at the one thing software is good at: a landmark placed two pixels off changes an angle by degrees, and your hand is not steadier than a face mesh.",
      },
    ],
    pick: "Fazly, narrowly, and for one reason: it is the only option here that shows you the distance it measured, the target it scored against and the weight that measurement carried. That does not make its numbers more correct than anybody else's. It makes them checkable, which is the only property in this category that a reader can actually verify.",
    method:
      "Ordered by how much of its working each option shows the reader, then by how much it costs to try. Not by score accuracy, because accuracy against facial attractiveness is not measurable and any list claiming to rank on it is inventing the ranking.",
    faq: [
      {
        q: "What is the best free looksmaxxing app?",
        a: "Fazly's browser tools are free with no account, no email and no install, and they run entirely on your device. Most apps in this category have a free scan and charge for the breakdown behind it, which is a different meaning of free and worth knowing before you start.",
      },
      {
        q: "Do looksmaxxing apps actually work?",
        a: "They measure reliably and they predict nothing. A face mesh finds the same landmarks on the same photograph every time, so the ratios are real measurements. What no app can do is tell you those ratios are what makes a face attractive, because that has never been established.",
      },
      {
        q: "Which looksmaxxing app is most accurate?",
        a: "The question has no answer, and any list that gives you one is guessing. There is no ground truth for facial attractiveness to be accurate against. The checkable question is which app shows you its measurements and its targets, and that one does have an answer.",
      },
    ],
  },

  "face-analysis-apps": {
    title: "Best Face Analysis Apps in 2026 — Honest Comparison | Fazly",
    description:
      "Five face analysis options, from instant browser measurement to a written professional report. What each one is for, what it cannot do, and which to pick for your question.",
    h1: "The best face analysis apps",
    standfirst:
      "Five options that answer genuinely different questions. Start by deciding which question is yours.",
    answer:
      "Face analysis splits into two products that get shelved together and should not be. One is measurement: a face mesh finds landmarks, arithmetic turns them into ratios, and you get a number in seconds. Fazly, UMAX, LooksMax AI and Mirror all do that, differing mainly in how much of the working they show. The other is interpretation: a written assessment of a specific face by someone who does this professionally, which is what Qoves is known for and what no automated tool produces well. If your question is what your canthal tilt is, use a measuring tool and use a free one. If your question is what a change would actually involve for your face, a measurement cannot answer it and a report can. Most people searching this phrase want the second thing and buy the first.",
    disclosure: DISCLOSURE,
    entries: [
      {
        name: "Qoves",
        bestFor:
          "Your question is interpretive — what a change would involve, what a clinician would notice — and you want prose from someone who does facial aesthetics for a living.",
        limitation:
          "Not instant and not free. For a question that a ratio answers in two seconds, it is the wrong instrument entirely.",
        compare: "qoves",
      },
      { ...SELF, tool: "looksmax-test" },
      {
        name: "UMAX",
        bestFor:
          "You want the category's standard result format, which is genuinely easier to read than a new one because you have seen it before.",
        limitation:
          "Its scoring cannot be inspected, so the result is something to accept rather than something to examine.",
        compare: "umax",
      },
      {
        name: "LooksMax AI",
        bestFor:
          "You want a focused single-purpose app rather than eight measurements and their weights.",
        limitation:
          "Focus costs you the working. One number is easier to read and impossible to check.",
        compare: "looksmax-ai",
      },
      {
        name: "Aire",
        bestFor:
          "You prefer its interface or its result format, which is a legitimate reason to choose an app and an underrated one.",
        limitation:
          "As with the rest of the category, the model behind the score is not open to inspection.",
        compare: "aire",
      },
    ],
    pick: "Qoves, if your question is interpretive — and most people typing this phrase have an interpretive question. A written assessment of your specific face is a thing software does not do, and paying for the thing you actually wanted beats getting a free number that answers something else. If your question is a measurement, take the free one and keep your money.",
    method:
      "Ordered by which question each option answers best, not by which is most popular. The first entry is the one most searchers of this phrase actually want, which is why it is not ours.",
    faq: [
      {
        q: "What is the most accurate face analysis app?",
        a: "No app can support an accuracy claim, because there is no agreed ground truth for facial attractiveness to be measured against. Landmark detection itself is reliable and reproducible; every interpretation layered on top of it is a convention somebody chose.",
      },
      {
        q: "Is there a free face analysis app?",
        a: "Fazly's browser tools are free and need no account. Several apps offer a free first scan and charge for the breakdown, which is worth checking before you photograph yourself.",
      },
      {
        q: "Can a face analysis app replace a professional consultation?",
        a: "No. A measurement is a distance and an angle. A consultation is a person interpreting a face in context, including the parts a landmark model does not see, and that is a different activity rather than a more expensive one.",
      },
    ],
  },

  "free-face-rating-sites": {
    title: "Best Free Face Rating Sites and Apps in 2026 | Fazly",
    description:
      "Which free face rating sites and apps work without an account, which send your photo to a server, and which show you the measurements behind the score. An honest list from one of them.",
    h1: "The best free face rating sites and apps",
    standfirst:
      "Free means different things here. The important difference is where your photograph goes.",
    answer:
      "Free face rating splits on a question most lists never mention: whether the photograph leaves your device. A browser tool that runs the face mesh as WebAssembly does the arithmetic locally, and you can confirm that in your browser's network tab — there is no request carrying the image because there is nowhere for it to go. A site that returns a score from a server has your photograph. Both can be free; only one is private. The second difference is what free covers. Fazly's browser tools are free with no account, no email and no install. Several apps advertise a free scan and charge for the breakdown behind it, which is a different offer worth reading before you upload a face. Everything else — the score, the ranking, the percentile — is a convention somebody picked.",
    disclosure: DISCLOSURE,
    entries: [
      { ...SELF, tool: "attractiveness-test" },
      {
        name: "UMAX",
        bestFor:
          "You want the result format the category is built around and the largest community to compare it with — check its own listing for what is free today.",
        limitation:
          "The model that produces the score cannot be inspected, so a result you disagree with is a result you cannot investigate.",
        compare: "umax",
      },
      {
        name: "LooksMax AI",
        bestFor:
          "You want one free number quickly from an app built around this exact search.",
        limitation:
          "The score arrives without its working, so a surprising result is a dead end rather than a starting point.",
        compare: "looksmax-ai",
      },
      {
        name: "Mirror",
        bestFor:
          "You want the simplest possible free result and no interest in ratios.",
        limitation:
          "There is nothing underneath the number to examine, by design.",
        compare: "mirror",
      },
      {
        name: "A photo, a ruler and the golden ratio",
        bestFor:
          "You want to know what these sites are actually doing, which is less than you would guess.",
        limitation:
          "It takes an hour instead of two seconds, and hand-placed landmarks are far less repeatable than a face mesh — the arithmetic is trivial, the measuring is not.",
      },
    ],
    pick: "Fazly, on the one property this list can actually verify: the browser tools need no account and never transmit the image, and you can confirm the second half yourself in the network tab rather than believing a privacy page. On the scores themselves nobody here — us included — can claim to be more correct than anybody else.",
    method:
      "Ordered by what free actually covers, then by whether the photograph leaves your device. Not by score, because no list can rank these on score without inventing the ranking.",
    faq: [
      {
        q: "Are free face rating sites safe?",
        a: "It depends entirely on whether the photograph is uploaded. A tool running in your browser never sends it; a site returning a server-computed score has it. Open the network tab while you use one — the answer is visible in about ten seconds and does not require trusting anybody.",
      },
      {
        q: "Which face rating site does not require signup?",
        a: "Fazly's eleven browser tools need no account, no email and no install. Most app-based options want at least an install and often an account before the first result.",
      },
      {
        q: "Are the scores on these sites real?",
        a: "The measurements are real: distances and angles from a landmark model, reproducible on the same photograph. The score built on top of them is a weighted distance from targets somebody chose, and it is not a finding about attractiveness no matter how it is presented.",
      },
    ],
  },

  "face-shape-detectors": {
    title: "Best Face Shape Detectors in 2026 — What They Get Wrong | Fazly",
    description:
      "Face shape detectors compared, including the option that is not software. Which ones show the ratios behind the verdict, and why the answer matters less than you think.",
    h1: "The best face shape detectors",
    standfirst:
      "Four options and an honest warning: the six-shape system is a convention, not a discovery.",
    answer:
      "Every face shape detector sorts you into the same six boxes — oval, round, square, heart, diamond, oblong — and that system was invented for selling haircuts, not found in any anatomical study. Faces sit on a continuum, and a great many land close enough to two boxes that the verdict flips between tools or between photographs. That is the single most useful thing to know before you use one. What separates a good detector from a bad one is therefore not the label but whether it shows the ratios behind it: face length over width, cheekbone over jaw, forehead over cheekbone, jaw angle. With those four numbers you can see how close the call was and disagree with it. Without them you have a word and no way to tell whether it was decided by a millimetre.",
    disclosure: DISCLOSURE,
    entries: [
      {
        name: "A hairdresser who cuts hair for a living",
        bestFor:
          "The real question behind this search, which is almost always which haircut, which glasses or which beard — and that question is answered by someone looking at your whole head, not by a label.",
        limitation:
          "Costs money and an appointment, and gives you no number to track. For curiosity rather than a decision, it is overkill.",
      },
      { ...SELF, tool: "face-shape" },
      {
        name: "Hairstyle and glasses try-on apps",
        bestFor:
          "You want the recommendation rather than the classification — the point of knowing your face shape is usually what to do next.",
        limitation:
          "Their classifier is a means to an end, so it is rarely shown and rarely explained; the recommendation is the product.",
      },
      {
        name: "UMAX and the general face-scan apps",
        bestFor:
          "You want face shape as one line inside a broader scan rather than as its own answer.",
        limitation:
          "One line in a long report, with no ratios attached, which means no way to tell a confident call from a coin flip.",
        compare: "umax",
      },
      {
        name: "A tape measure and four divisions",
        bestFor:
          "You want to know exactly how arbitrary the answer is. Measure length, cheekbone width, jaw width and forehead width, then divide.",
        limitation:
          "Measuring your own head with a tape is genuinely difficult and the errors are large — which is itself the lesson.",
      },
    ],
    pick: "A hairdresser, for most people asking. The search is almost never about the label; it is about what to do with your hair, your glasses or your beard, and a person looking at your actual head answers that better than any classifier. Use a detector to see the four ratios and how close the call was — then take the ratios, not the word, to whoever is cutting.",
    method:
      "Ordered by how well each option answers the question people are actually asking, which is a styling question rather than a classification question. Tools that show the underlying ratios rank above tools that show only the verdict.",
    faq: [
      {
        q: "How accurate are face shape detectors?",
        a: "They measure accurately and classify arbitrarily. The ratios are real and reproducible; the boundaries between the six shapes are conventions somebody drew, and a face near a boundary will genuinely get different answers from different tools.",
      },
      {
        q: "Why do face shape detectors disagree with each other?",
        a: "Because they are drawing the boundaries in different places. Two tools can measure identical ratios and still return oval and oblong if one puts the threshold at 1.5 and the other at 1.55. Neither is wrong — there is no correct threshold to be right about.",
      },
      {
        q: "What is the rarest face shape?",
        a: "There is no reliable answer, because there is no population study using consistent thresholds. Numbers circulating for this question trace back to styling articles rather than to measurement, and they disagree with each other.",
      },
    ],
  },

  "jawline-apps": {
    title: "Best Jawline Apps in 2026 — And What None of Them Do | Fazly",
    description:
      "Jawline apps measure, track and coach. None of them change a jawline. An honest list of what each one is for, and what actually moves the number.",
    h1: "The best jawline apps",
    standfirst:
      "Measuring, tracking and coaching are three different products. Changing your jaw is not one of them.",
    answer:
      "No app changes a jawline, and the useful list is therefore a list of what apps do instead. Measuring is one: a face mesh gives you a gonial angle and a jaw-to-cheekbone ratio in seconds, which is a real reproducible number and a sensible baseline. Tracking is another: the same measurement monthly, from the same distance in the same light, is the only way to know whether anything actually moved. Coaching is a third, and it is where the category oversells — an app can hold you to a routine, and a routine of sleep, body composition and posture is genuinely what changes how a jaw reads. What does not change adult jaw bone is chewing gum, jaw exercisers or an app. Body fat, swelling, posture and camera angle change how the jaw looks, and they account for nearly every before-and-after you have seen.",
    disclosure: DISCLOSURE,
    entries: [
      {
        name: "An orthodontist or maxillofacial surgeon",
        bestFor:
          "You want the jaw itself to be different. This is the only entry on the list that changes bone, and it is a medical decision rather than a purchase.",
        limitation:
          "Expensive, slow and genuinely serious. For almost everybody searching this phrase it is far more than the question needs.",
      },
      { ...SELF, tool: "jawline" },
      {
        name: "General face-scan apps",
        bestFor:
          "You want jawline as one score inside a broader result you were getting anyway.",
        limitation:
          "A jaw score with no gonial angle behind it cannot be tracked, because you cannot tell a real change from a different camera angle.",
        compare: "umax",
      },
      {
        name: "Habit and routine trackers",
        bestFor:
          "The things that actually move the number — sleep, body composition, posture, hydration — are habits, and a habit app is the correct tool for a habit.",
        limitation:
          "It measures nothing about your face, so it cannot tell you whether any of it worked.",
      },
      {
        name: "Jaw exercisers and chewing devices",
        bestFor:
          "Masseter hypertrophy is real; the muscle does grow with resistance, and some people want that.",
        limitation:
          "It does not change the mandible, and a larger masseter widens the lower face — which is the opposite of what most people buying one are trying to achieve.",
      },
    ],
    pick: "None of them, if the goal is a different jaw — that is an orthodontic question and no app touches it. If the goal is knowing whether anything you are doing works, measure the gonial angle monthly from the same distance in the same light and watch the number rather than the mirror. Fazly's jawline tool is free and shows the angle, which is all this job needs.",
    method:
      "Ordered by what each option actually changes, most to least. An app that cannot change anything is still useful for measuring, so measuring tools rank above coaching that claims more.",
    faq: [
      {
        q: "Do jawline apps work?",
        a: "They work as measuring instruments and not as treatments. A gonial angle from a face mesh is a real number you can track. No app has been shown to change the bone underneath it, and none can.",
      },
      {
        q: "Do jawline exercises actually change your jaw?",
        a: "They grow the masseter, which is a muscle and responds to resistance like any other. They do not remodel the mandible. A bigger masseter makes the lower face wider, so the visible result is often the opposite of the intended one.",
      },
      {
        q: "What actually changes how a jawline looks?",
        a: "Body fat first, by a wide margin, then swelling and sleep, then posture, then the camera. Head tilt alone moves a measured gonial angle by several degrees, which is why a before-and-after taken at different angles proves nothing at all.",
      },
    ],
  },

  "mewing-apps": {
    title: "Best Mewing Apps in 2026 — And Whether Any of It Works | Fazly",
    description:
      "Mewing apps, timers and trackers compared honestly, starting with what the evidence actually supports about tongue posture in adults.",
    h1: "The best mewing apps",
    standfirst:
      "Read the first entry before you install anything. It is the one that answers the question.",
    answer:
      "Tongue posture does not remodel adult facial bone, and no app changes that. The claim originates with orthodontic work on children whose facial growth was still in progress, where jaw development genuinely can be influenced; extending it to a twenty-five-year-old is an extrapolation nobody has demonstrated. What correct tongue posture does support is real but smaller: swallowing pattern, mouth breathing and the tongue position that keeps an airway open, all of which are legitimate clinical concerns and all of which are treated by a myofunctional therapist rather than by an app. So the honest list is short. Reminder apps are good at reminders. Measurement tools give you a gonial angle to check against, which is how you find out that nothing moved. And a therapist addresses the thing that actually has evidence behind it, which is usually not the thing people came for.",
    disclosure: DISCLOSURE,
    entries: [
      {
        name: "A myofunctional therapist or orthodontist",
        bestFor:
          "The parts of this that have evidence: swallowing pattern, mouth breathing, airway and tongue tie. If any of those apply to you, this is the entry that matters.",
        limitation:
          "It will not promise you a jawline, because the clinical literature does not support one in an adult.",
      },
      {
        name: "Any reminder or habit app you already have",
        bestFor:
          "Tongue posture is a habit, and a habit needs a reminder rather than a dedicated app. A repeating alarm does the entire job for free.",
        limitation:
          "It measures nothing, so it cannot tell you whether the habit produced anything.",
      },
      { ...SELF, tool: "jawline" },
      {
        name: "Dedicated mewing apps",
        bestFor:
          "You want the posture instructions and a streak in one place, and the structure genuinely helps some people stick to a habit.",
        limitation:
          "The category is built around a before-and-after that adult bone does not do, and progress photos taken at different angles will show you a change that is not there.",
      },
      {
        name: "A camera on a tripod at a fixed distance",
        bestFor:
          "Actually testing the claim on yourself: same distance, same lens, same light, same head position, once a month.",
        limitation:
          "It requires the discipline the app was supposed to supply, and the honest result after six months is usually no measurable change.",
      },
    ],
    pick: "A myofunctional therapist, if you have a swallowing, breathing or airway concern — that is the part of this with evidence behind it and it is a real clinical problem worth solving. If you just want to try tongue posture, the reminder you already have on your phone does the job, and measuring your gonial angle monthly is how you find out what happened. Our guide to doing it correctly says the same thing at more length.",
    method:
      "Ordered by evidence. The first entry treats the thing that has been demonstrated; the last is the only way to check the thing that has not.",
    faq: [
      {
        q: "Do mewing apps actually work?",
        a: "As habit reminders, yes. As a way to change adult facial bone, no — that is not something tongue posture has been shown to do, and an app cannot change the underlying claim.",
      },
      {
        q: "How long does mewing take to work?",
        a: "It does not, in the sense the question means. There is no timeline for adult bone remodelling from tongue posture because there is no demonstrated effect to put a timeline on. Swallowing and breathing patterns can improve with therapy in months.",
      },
      {
        q: "Is mewing safe?",
        a: "Resting your tongue on the palate is harmless. Forceful jaw clenching, aggressive thrusting or anything that causes jaw pain is not, and jaw pain is a reason to stop and see a dentist rather than to push harder.",
      },
    ],
  },
};
